<template>
  <div class="v2-connections-page">
    <div
      v-if="loading && !tableItems.length"
      class="v2-page-loading"
    >
      <v2-spinner
        size="lg"
        label="Loading connections..."
      />
    </div>

    <template v-else>
      <!-- Header -->
      <div class="v2-flex v2-justify-between v2-items-center v2-mb-4">
        <h1 class="v2-heading-sm">
          My Retailers
        </h1>
        <v2-button
          v-if="isBrand"
          @click="showAddModal = true"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line
              x1="12"
              y1="5"
              x2="12"
              y2="19"
            />
            <line
              x1="5"
              y1="12"
              x2="19"
              y2="12"
            />
          </svg>
          Add a Retailer
        </v2-button>
      </div>

      <!-- Tabs -->
      <v2-tabs
        :tabs="tabsList"
        :active-tab="selectedTab"
        @tab-change="onLoadTab"
      />

      <!-- Filters row -->
      <div
        v-if="tableLength || isShowImportExport"
        class="v2-flex v2-items-center v2-gap-4 v2-mb-4 v2-connections-page__filters"
      >
        <div
          v-if="tableLength"
          class="v2-connections-page__search"
        >
          <svg
            class="v2-connections-page__search-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle
              cx="11"
              cy="11"
              r="8"
            />
            <line
              x1="21"
              y1="21"
              x2="16.65"
              y2="16.65"
            />
          </svg>
          <v2-input
            :value="search"
            :placeholder="getTextForPlaceholder"
            @input="onSearchInput"
          />
        </div>

        <v2-reps-filter-dropdown
          v-if="tableLength && (isEntityAdmin || isSalesManager) && isBrand && !loading && selectedTab === CONNECTION_TABS.DIRECT"
          :options="salesReps"
          :show-self-option="true"
          @on-select-options="onFilterBySalesRep"
        />

        <v2-reps-filter-dropdown
          v-if="tableLength && isEntityAdmin && isBrand && !loading && selectedTab === CONNECTION_TABS.DIRECT"
          :options="salesManagers"
          placeholder="Sales manager"
          :show-self-option="true"
          @on-select-options="onFilterBySalesManager"
        />

        <v2-button
          v-if="isShowImportExport"
          variant="outline"
          class="v2-connections-page__import-btn"
          @click="showImportModal = true"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line
              x1="12"
              y1="3"
              x2="12"
              y2="15"
            />
          </svg>
          Import
        </v2-button>
      </div>

      <!-- Empty state -->
      <div
        v-if="!loading && !filteredItems.length"
        class="v2-flex v2-justify-center v2-p-12"
      >
        <v2-card class="v2-text-center v2-p-8">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="v2-connections-page__empty-icon"
          >
            <line
              x1="22"
              y1="2"
              x2="11"
              y2="13"
            />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
          <p class="v2-text-muted v2-mt-4">
            There are no connections yet.
          </p>
        </v2-card>
      </div>

      <!-- Table -->
      <template v-else>
        <v2-card :no-padding="true">
          <v2-table
            :columns="tableFields"
            :items="paginatedItems"
            :loading="loading"
          >
            <!-- Head overrides -->
            <template #cell(salesRepName)="{ item }">
              <span class="v2-text-nowrap">
                {{ item.isCreatedBySelf ? profile.entityName : item.salesRepName }}
              </span>
            </template>

            <template #cell(firstOrderDate)="{ item }">
              <div
                v-if="item.firstOrderDate"
                class="v2-flex v2-items-center v2-gap-1"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="v2-connections-page__clock-icon"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                  />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                {{ item.firstOrderDate }}
              </div>
            </template>

            <template #cell(lastOrderDate)="{ item }">
              <div
                v-if="item.lastOrderDate"
                class="v2-flex v2-items-center v2-gap-1"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="v2-connections-page__clock-icon"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                  />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                {{ item.lastOrderDate }}
              </div>
            </template>

            <template #cell(nickName)="{ item }">
              <v2-add-nick-name
                :data="{ item }"
                @load-connections="getData()"
              />
            </template>

            <template #cell(socialLinks)="{ item }">
              <div class="v2-flex v2-gap-2">
                <a
                  v-if="item.socialLinks.instagramLink"
                  :href="item.socialLinks.instagramLink"
                  target="_blank"
                  class="v2-connections-page__social-link"
                  title="Instagram"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect
                      x="2"
                      y="2"
                      width="20"
                      height="20"
                      rx="5"
                      ry="5"
                    />
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                    <line
                      x1="17.5"
                      y1="6.5"
                      x2="17.51"
                      y2="6.5"
                    />
                  </svg>
                </a>
                <a
                  v-if="item.socialLinks.facebookLink"
                  :href="item.socialLinks.facebookLink"
                  target="_blank"
                  class="v2-connections-page__social-link"
                  title="Facebook"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                  </svg>
                </a>
                <a
                  v-if="item.socialLinks.linkedinLink"
                  :href="item.socialLinks.linkedinLink"
                  target="_blank"
                  class="v2-connections-page__social-link"
                  title="LinkedIn"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                    <rect
                      x="2"
                      y="9"
                      width="4"
                      height="12"
                    />
                    <circle
                      cx="4"
                      cy="4"
                      r="2"
                    />
                  </svg>
                </a>
                <a
                  v-if="item.socialLinks.twitterLink"
                  :href="item.socialLinks.twitterLink"
                  target="_blank"
                  class="v2-connections-page__social-link"
                  title="Twitter"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                  </svg>
                </a>
                <a
                  v-if="item.socialLinks.websiteLink"
                  :href="item.socialLinks.websiteLink"
                  target="_blank"
                  class="v2-connections-page__social-link"
                  title="Website"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                    />
                    <line
                      x1="2"
                      y1="12"
                      x2="22"
                      y2="12"
                    />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                </a>
              </div>
            </template>

            <template #cell(teammates)="{ item }">
              <button
                class="v2-connections-page__view-link"
                @click="openTeammatesModal(item)"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle
                    cx="9"
                    cy="7"
                    r="4"
                  />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                View
              </button>
            </template>

            <template #cell(actions)="{ item }">
              <button
                class="v2-connections-page__action-btn"
                @click="onEditUser(item)"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            </template>

            <template #cell(discount)="{ item }">
              {{ getDicountWithPercent(item.discount) }}%
            </template>

            <template #cell(paymentTerms)="{ item }">
              <span class="v2-text-nowrap">{{ item.paymentTerms }}</span>
            </template>
          </v2-table>
        </v2-card>

        <v2-pagination
          :current-page="currentPage"
          :total-items="filteredItemsCount"
          :per-page="perPage"
          @page-change="onPageChange"
        />
      </template>
    </template>

    <!-- Modals -->
    <v2-add-user-modal
      :show="showAddModal"
      @close="showAddModal = false"
      @load-connections="getData()"
    />

    <v2-edit-user-modal
      :show="showEditModal"
      :user-details="userData"
      :load-connections="getData"
      @close="showEditModal = false"
    />

    <v2-connection-request-modal
      :show="showConnectionRequest"
      :brand-id="brandId"
      :retailer-id="retailerId"
      :request-id="requestId"
      :status="status"
      :request-message="requestMessage"
      @close="showConnectionRequest = false"
      @update-request="getData()"
    />

    <v2-import-modal
      :show="showImportModal"
      :import-file="directRetailersImportFile"
      :is-exporting="isExporting"
      :is-uploading="isUploadingDirectRetailers"
      @close="closeImportModal"
      @export="exportDirectUsers"
      @file-selected="onFileSelected"
      @upload="uploadDirectRetailers"
    />

    <v2-retailer-teammates-modal
      ref="retailerTeammatesModal"
      :show="showTeammatesModal"
      :retailer-id="selectedRetailerIdForTeammates"
      :retailer-name="selectedRetailerName"
      @close="showTeammatesModal = false"
      @open-invite="showInviteModal = true"
    />

    <v2-invite-teammate-modal
      :show="showInviteModal"
      :retailer-id="selectedRetailerIdForTeammates"
      @close="showInviteModal = false"
      @invited="onTeammateInvited"
    />
  </div>
</template>

<script>
import { computed, ref } from '@vue/composition-api'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Card from '@/components/v2/V2Card.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Tabs from '@/components/v2/V2Tabs.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Table from '@/components/v2/V2Table.vue'
import V2Pagination from '@/components/v2/V2Pagination.vue'
import { useConnections } from '@/composables/useConnections'
import { apiToastSuccess, apiToastWarning } from '@/@core/utils/toast'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import { UPLOAD_DOCUMENTS } from '@/store/modules/common.module'
import { IMPORT_DIRECT_USERS, VALIDATE_RETAILER_IMPORT_FILE } from '@/store/modules/connections.module'

import V2AddNickName from './V2AddNickName.vue'
import V2RepsFilterDropdown from './V2RepsFilterDropdown.vue'
import V2RetailerTeammatesModal from './V2RetailerTeammatesModal.vue'
import V2InviteTeammateModal from './V2InviteTeammateModal.vue'
import V2ConnectionRequestModal from './V2ConnectionRequestModal.vue'
import V2ImportModal from './V2ImportModal.vue'
import V2AddUserModal from './V2AddUserModal.vue'
import V2EditUserModal from './V2EditUserModal.vue'

const { FILE_TYPES } = c

export default {
  name: 'V2Connections',
  components: {
    V2Spinner,
    V2Card,
    V2Button,
    V2Tabs,
    V2Input,
    V2Table,
    V2Pagination,
    V2AddNickName,
    V2RepsFilterDropdown,
    V2RetailerTeammatesModal,
    V2InviteTeammateModal,
    V2ConnectionRequestModal,
    V2ImportModal,
    V2AddUserModal,
    V2EditUserModal,
  },
  setup() {
    const conn = useConnections()
    conn.initConnections()

    // Modal visibility refs
    const showAddModal = ref(false)
    const showEditModal = ref(false)
    const showConnectionRequest = ref(false)
    const showImportModal = ref(false)
    const showTeammatesModal = ref(false)
    const showInviteModal = ref(false)

    const tabsList = [
      { key: conn.CONNECTION_TABS.DIRECT, label: 'Direct' },
      { key: conn.CONNECTION_TABS.MARKETPLACE, label: 'Marketplace' },
    ]

    // Client-side search filtering
    const filteredItems = computed(() => {
      if (!conn.search.value) return conn.tableItems.value
      const q = conn.search.value.toLowerCase()
      return conn.tableItems.value.filter(item => conn.filterIncludedFields.some(
        field => String(item[field] || '').toLowerCase().includes(q),
      ))
    })

    const filteredItemsCount = computed(() => filteredItems.value.length)

    const paginatedItems = computed(() => {
      const start = (conn.currentPage.value - 1) * conn.perPage.value
      return filteredItems.value.slice(start, start + conn.perPage.value)
    })

    return {
      ...conn,
      showAddModal,
      showEditModal,
      showConnectionRequest,
      showImportModal,
      showTeammatesModal,
      showInviteModal,
      tabsList,
      filteredItems,
      filteredItemsCount,
      paginatedItems,
    }
  },
  methods: {
    onSearchInput(value) {
      this.search = value
      this.currentPage = 1
    },
    onPageChange(page) {
      this.currentPage = page
    },
    onEditUser(item) {
      this.editUser(item)
      this.showEditModal = true
    },
    openTeammatesModal(item) {
      this.setTeammatesTarget(item)
      analytics.track(constants.TRACKS.ACTIONS.RETAILER_TEAMMATES.BRAND_CLICKS_VIEW_TEAMMATES, {
        retailerId: item.retailerId,
        retailerName: item.entityName,
      })
      this.showTeammatesModal = true
    },
    onTeammateInvited() {
      this.$refs.retailerTeammatesModal.refreshTeammates()
    },
    onFileSelected(file) {
      this.directRetailersImportFile = file
    },
    closeImportModal() {
      this.resetImportState()
      this.showImportModal = false
    },
    async ensureFileUploaded() {
      if (!this.directRetailersImportFile) {
        throw new Error('Please select a file')
      }
      if (!this.importFileUrl) {
        this.importFileUrl = await this.$store.dispatch(
          UPLOAD_DOCUMENTS,
          { file: this.directRetailersImportFile, fileType: FILE_TYPES.DIRECT_RETAILERS },
        )
      }
      return this.importFileUrl
    },
    async hasCurrencyChangeConflict(payload) {
      const response = await this.$store.dispatch(VALIDATE_RETAILER_IMPORT_FILE, { payload })
      return !!response?.data?.data?.hasCurrencyChange
    },
    confirmCurrencyOverride() {
      // eslint-disable-next-line no-alert
      return window.confirm('Changing the transaction currency will remove all linked price books. Previous order values will remain unchanged. Are you sure you want to continue?')
    },
    async importDirectUsers(payload) {
      const params = { shouldUpdateExistingUser: true }
      const result = await this.$store.dispatch(IMPORT_DIRECT_USERS, { payload, params })
      apiToastSuccess(result.data.message)
      this.closeImportModal()
      this.getData()
    },
    async uploadDirectRetailers() {
      try {
        this.isUploadingDirectRetailers = true
        const fileUrl = await this.ensureFileUploaded()
        const payload = { file: fileUrl }
        const hasConflict = await this.hasCurrencyChangeConflict(payload)
        if (hasConflict && !this.confirmCurrencyOverride()) {
          return
        }
        await this.importDirectUsers(payload)
      } catch (err) {
        apiToastWarning(err || 'An error occurred while importing direct retailers')
      } finally {
        this.isUploadingDirectRetailers = false
        this.importFileUrl = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-connections-page {
  &__filters {
    flex-wrap: wrap;
  }

  &__search {
    position: relative;
    min-width: 250px;
    flex: 1;
    max-width: 350px;
  }

  &__import-btn {
    margin-left: auto;
  }

  &__search-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--v2-muted-foreground);
    z-index: 1;
    pointer-events: none;
  }

  &__empty-icon {
    color: var(--v2-muted-foreground);
    margin: 0 auto;
    display: block;
  }

  &__social-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    color: var(--v2-muted-foreground);
    transition: color 0.15s ease;

    &:hover {
      color: var(--v2-primary);
    }
  }

  &__clock-icon {
    color: var(--v2-primary);
    flex-shrink: 0;
  }

  &__view-link {
    display: inline-flex;
    align-items: center;
    gap: var(--v2-space-1);
    background: none;
    border: none;
    color: var(--v2-primary);
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    padding: 0;
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
    }
  }

  &__action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-1);
    background: transparent;
    border: none;
    border-radius: var(--v2-radius-sm);
    color: var(--v2-muted-foreground);
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: var(--v2-muted);
      color: var(--v2-primary);
    }
  }
}

.v2-text-nowrap {
  white-space: nowrap;
}
</style>
